import React, { useEffect, useState } from "react";
import { Container } from "react-bootstrap";
import Stack from "react-bootstrap/Stack";
import {
  searchCaseList,
  advanceSearchCaseList,
} from "../../../../services/CaseInformationApi";
import useBreadcrumbs from "../../../../hooks/useBreadCrumbs";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../../redux/Store";
import DataTable from "../../../../common/DataTable";
import CustomPagination from "../../../../common/Pagination";
import TableRowsPerPage from "../../../../common/TableRowsPerPage";
import { SEARCHCASE_HEADERS } from "../actions/Constant";
import { clearSearchQuery } from "../../../../redux/slice/searchSlice";
import Loader from "../../../../common/Loader";
import CreateNewCase from "../../Case_Information/index";
import { useLocation } from "react-router-dom";
import moment from "moment";
import { setIsAdvanceSearch } from "../../../../redux/slice/AdvanceSearchSlice";
import { setIsSearchEditable } from "../../../../redux/slice/CaseHeaderSlice";

const SearchCase: React.FC = () => {
  const breadcrumbs = useBreadcrumbs();
  const dispatch = useDispatch();
  const location = useLocation();

  const loggedInUserid = useSelector((state: RootState) => state.auth.userid);
  const searchQuery = useSelector((state: RootState) => state.searchcase.query);

  const [filteredData, setFilteredData] = useState<any[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(10);
  const [loading, setLoading] = useState<boolean>(false);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [totalRows, setTotalRows] = useState<number>(0);
  const [sortKey, setSortKey] = useState<string>("");
  const [sortDirection, setSortDirection] = useState<string>("DESC");
  const [open, setOpen] = useState<boolean>(false);
  const [action, setAction] = useState<string>("");
  const [selectedUserData, setSelectedUserData] = useState<any>(null);

  const { isDashboard, newCaseInFoID } = useSelector(
    (state: RootState) => state.caseHeader
  );

  const { InterVal } = useSelector((state: RootState) => state.DashBoardStage);

  useEffect(() => {
    if (isDashboard && newCaseInFoID) {
      setAction("Edit");
      setSelectedUserData({ caseInfoId: newCaseInFoID });
      setOpen(true);
    } else if (InterVal) {
      searchcase();
    }
  }, [isDashboard, InterVal]);

  // ✅ Clear search query from Redux on component mount
  useEffect(() => {
    dispatch(clearSearchQuery());
  }, [dispatch]);
  useEffect(() => {
    if (location.state?.caseInfoID) {
      setOpen(true);
      setAction("Edit");
      setSelectedUserData({
        caseInfoId: location.state?.caseInfoID,
        caseId: location.state?.caseId,
        masterClaimNumber: location.state?.claimNumber,
      });
    }
  }, [location.state]);

  useEffect(() => {
    if (location.state?.resetPage) {
      setCurrentPage(1); // ✅ Just set the state, don't call searchcase() yet
    }
  }, [location.state]);

  useEffect(() => {
    if (searchQuery.length >= 3 || searchQuery.length === 0) {
      searchcase(); // ✅ Runs only after `currentPage` is updated
    }
  }, [searchQuery, currentPage, itemsPerPage, sortKey, sortDirection]);

  const formattedDateTime = (date: string | Date): string | null => {
    if (!date) {
      console.error("Invalid input: date is required.");
      return null;
    }

    const formattedDate = moment(date).format("MM/DD/YYYY");
    return `${formattedDate}`;
  };

  const searchcase = async () => {
    setLoading(true);

    const filterParams = {
      searchString: searchQuery,
      pageNo: currentPage,
      pageSize: itemsPerPage,
      sortColumn: sortKey,
      sortOrder: sortDirection,
      triggerById: loggedInUserid,
    };

    const params = {
      caseId: "",
      claimNumber: "",
      wcabNumber: "",
      panelNumber: "",
      fileNumber: "",
      caseName: "",
      caseNumber: "",
      adjNumber: "",
      subjectName: "",
      ssn: "",
      employerStakeholderIds: "",
      priorityIds: "",
      caseReviewTypeIds: "",
      caseStageIds: "",
      injuryTypeIds: "",
      severityOfInjuryIds: "",
      dateOfRegistrationFrom: null,
      dateOfRegistrationTo: null,
      dateOfIncidentFrom: null,
      dateOfIncidentTo: null,
      clientIds: "",
      applicantAttorneyIds: "",
      defenseAttorneyIds: "",
      insuranceIds: "",
      vendorIds: "",
      appointmentConfirmationNo: "",
      dateOfAppointmentStart: null,
      dateOfAppointmentEnd: null,
      startTime: null,
      appointmentStatusIds: "",
      medicareIds: "",
      qMedDoctorIds: "",
      appointmentCategoryIds: "",
      appointmentTypeIds: "",
      isInterpreterRequired: 0,
      medicalExamTypeId: 0,
      documentTypeIds: "",
      documentCategoryText: "",
      fileName: "",
      addlFileDesc: "",
      uploadedByName: "",
      uploadDateTime: null,
      additionalComments: "",
      pageNumber: currentPage,
      pageSize: itemsPerPage,
      sortColumn: sortKey,
      sortOrder: sortDirection,
      tenantId: 0,
      triggerById: loggedInUserid,
    };
    try {
      // const caseList = await searchCaseList(filterParams);
      // const response = await advanceSearchCaseList(params);
      let caseList: any[] = [];
      if (InterVal) {
        caseList = await advanceSearchCaseList(params);
      } else {
        caseList = await searchCaseList(filterParams);
      }
      if (caseList?.length) {
        const formattedList = caseList.map((item: any) => ({
          ...item,
          dateOfExam: formattedDateTime(item.dateOfExam),
          caseSubjectDOB: formattedDateTime(item.caseSubjectDOB),
          caseTypeDesc: item.masterClaimNumber?.startsWith("UnAssigned Claim")
            ? ""
            : item.caseTypeDesc,
          masterClaimNumber: item.masterClaimNumber?.startsWith(
            "UnAssigned Claim"
          )
            ? ""
            : item.masterClaimNumber,
        }));

        setFilteredData(formattedList);
        setTotalPages(formattedList[0]?.totalPages || 1);
        setTotalRows(formattedList[0]?.totalCount ?? 0);
      } else {
        setFilteredData([]);
        setTotalPages(1);
        setTotalRows(0);
        console.error("No data found in caseList");
      }
    } catch (error) {
      console.error("Error fetching caseList:", error);
      setFilteredData([]);
      setTotalPages(1);
      setTotalRows(0);
    } finally {
      setLoading(false);
    }
  };

  const handleSortAction = (row: any) => {
    setSortKey(row.key.toLowerCase());
    setSortDirection(row.direction);
  };

  const handleRowAction = (row: any, action: string) => {
    setAction(action);
    setOpen(true);
    setSelectedUserData(row);
    dispatch(setIsSearchEditable(true));
  };

  const paginate = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  const handleRowsPerPageChange = (value: number) => {
    setItemsPerPage(value);
    setCurrentPage(1);
  };

  const handleCancel = (status: string) => {
    setOpen(false);
    setCurrentPage(1);
    searchcase();
    dispatch(setIsSearchEditable(false));
  };

  // Corrected entries text calculation
  const entriesText = !totalRows
    ? "Showing 0 of 0 entries"
    : `Showing ${(currentPage - 1) * itemsPerPage + 1} to ${Math.min(
        currentPage * itemsPerPage,
        totalRows
      )} of ${totalRows} entries`;

  
  return !open ? (
    <div>
      <Loader isLoading={loading} />
      <div className="breadcrumbg breadcrumbgNew">
        <Container fluid className="px-5">
          <Stack direction="horizontal" gap={2}>
            <div className="pt-0 flex-column d-flex align-items-start fade-in-left">
              <h4>CASE MANAGEMENT</h4>
            </div>
          </Stack>
        </Container>
      </div>
      <div>
        <div className="main_wrapper_section">
          <Container fluid className="px-5">
            <div className="sectionTitle noborder me-auto py-3">List</div>
            <div className="table-responsiveNewSet">
              <DataTable
                headers={SEARCHCASE_HEADERS}
                rows={filteredData}
                onAction={handleRowAction}
                onSortAction={handleSortAction}
                checkInstruction={true}
              />
            </div>
            {/* Pagination and Rows Per Page Section */}
            <div className="d-flex justify-content-between align-items-center mt-3">
              <div className="col-md-12">
                <div className="row">
                  <div className="col-md-3 showingData_wrapper">
                    <span>{entriesText}</span>
                  </div>
                  <div className="col-md-9">
                    <div className="d-flex flex-row-reverse">
                      <CustomPagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={paginate}
                      />
                      <TableRowsPerPage
                        value={itemsPerPage}
                        onSelect={handleRowsPerPageChange}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Container>
        </div>
      </div>
    </div>
  ) : (
    <CreateNewCase
      userData={selectedUserData}
      action={action}
      onCancel={handleCancel}
      tabState=""
    />
  );
};

export default SearchCase;
